import { useEffect, useState } from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';

// Auth Context
import { useAuth } from './contexts/AuthContext';
import { useTenant } from './contexts/TenantContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { MessageProvider } from './contexts/MessageContext';

// Layouts
import Layout from './components/Layout/Layout';
import MemberSidebar from './components/Layout/MemberSidebar';
import AdminSidebar from './components/Layout/AdminSidebar';
import SuperAdminSidebar from './components/Layout/SuperAdminSidebar';
import StaffSidebar from './components/Layout/StaffSidebar';
import RoleBasedSidebar from './components/RoleBasedSidebar';

// Public Pages
import LandingPage from './pages/public/LandingPage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import RegisterTenantPage from './pages/tenant/RegisterTenantPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import MarketingMaterialsPage from './pages/public/MarketingMaterialsPage';

// Admin Pages
import DashboardPage from './pages/admin/DashboardPage';
import MemberListPage from './pages/admin/MemberListPage';
import MemberDetailPage from './pages/admin/MemberDetailPage';
import AddMemberPage from './pages/admin/AddMemberPage';
import TenantSettingsPage from './pages/admin/TenantSettingsPage';
import AnalyticsPage from './pages/admin/AnalyticsPage';
import MembershipAnalytics from './pages/admin/MembershipAnalytics';
import MemberSubscriptionPlans from './pages/admin/MemberSubscriptionPlans';
import MemberSubscriptionPlanForm from './pages/admin/MemberSubscriptionPlanForm';
import MemberSubscriptionPlanItem from './pages/admin/MemberSubscriptionPlanItem';
import AdminPlans from './pages/admin/AdminPlans';
import SubscriptionCheckout from './pages/admin/SubscriptionCheckout';
import SubscriptionManagement from './pages/admin/SubscriptionManagement';
import AdminDebtManagementPage from './pages/admin/AdminDebtManagementPage';
import ExpensesPage from './pages/admin/ExpensesPage';
import AdminFinancialRequestsPage from './pages/admin/FinancialRequests/AdminFinancialRequestsPage';
import AdminFinancialRequestDetailPage from './pages/admin/FinancialRequests/AdminFinancialRequestDetailPage';
import AdminSkillCoinDashboardPage from './pages/admin/SkillCoin/AdminSkillCoinDashboardPage';
import BulkMemberUploadPage from './pages/admin/BulkMemberUploadPage';
import FinancialReportScheduler from './pages/admin/FinancialReportScheduler';
import AdminMessagesPage from './pages/admin/Messages/AdminMessagesPage';
import AdminMessageDetailPage from './pages/admin/Messages/AdminMessageDetailPage';
import AdminComposePage from './pages/admin/Messages/AdminComposePage';
import WorkflowDashboard from './pages/admin/Reports/WorkflowDashboard';

// SuperAdmin Pages
import SuperAdminSetupPage from './pages/superadmin/SuperAdminSetupPage';
import AdminDashboard from './pages/superadmin/SuperAdminDashboard';
import SuperAdminPaymentSettings from './pages/superadmin/SuperAdminPaymentSettings';
import SuperAdminSubscriptions from './pages/superadmin/SuperAdminSubscriptions';
import SuperAdminPlanManagement from './pages/superadmin/SuperAdminPlanManagement';
import SuperAdminTenantsPage from './pages/superadmin/SuperAdminTenantsPage';
import SuperAdminTenantDetailPage from './pages/superadmin/SuperAdminTenantDetailPage';
import SuperAdminAddTenantPage from './pages/superadmin/SuperAdminAddTenantPage';
import AllUsers from './pages/superadmin/AllUsers';
import SuperAdminAddTenantAdminPage from './pages/superadmin/SuperAdminAddTenantAdminPage';

// Member Pages
import MemberDashboardPage from './pages/member/MemberDashboardPage';
import MemberProfilePage from './pages/member/MemberProfilePage';
import MemberHelpPage from './pages/member/MemberHelpPage';
import MemberDirectoryPage from './pages/member/MemberDirectoryPage';
import MemberPlans from './pages/member/MemberSubscriptionPlans';
import MySubscription from './pages/member/MySubscription';
import MemberSubscriptionCheckout from './pages/member/SubscriptionCheckout';
import PaymentHistory from './pages/member/PaymentHistory';
import TenantSubscriptionPlans from './pages/member/TenantSubscriptionPlans';
import SubscribeToTenantPlan from './pages/member/SubscribeToTenantPlan';
import MemberFinancialRequestsPage from './pages/member/FinancialRequests/MemberFinancialRequestsPage';
import CreateFinancialRequestPage from './pages/member/FinancialRequests/CreateFinancialRequestPage';
import MemberFinancialRequestDetailPage from './pages/member/FinancialRequests/MemberFinancialRequestDetailPage';
import MemberWalletDashboardPage from './pages/member/Wallet/MemberWalletDashboardPage';
import MemberSkillCoinDashboardPage from './pages/member/SkillCoin/MemberSkillCoinDashboardPage';
import TokenHistoryPage from './pages/member/SkillCoin/TokenHistoryPage';
import MemberReset from './pages/member/MemberReset';
import MemberMessagesPage from './pages/member/Messages/MemberMessagesPage';
import MemberMessageDetailPage from './pages/member/Messages/MemberMessageDetailPage';
import MemberMessageComposePage from './pages/member/Messages/MemberMessageComposePage';

// Import Workflow Pages
import MemberWorkflowsPage from './pages/member/Workflow/MemberWorkflowsPage';
import CreateWorkflowRequestPage from './pages/member/Workflow/CreateWorkflowRequestPage';
import MemberWorkflowDetailPage from './pages/member/Workflow/MemberWorkflowDetailPage';
import AdminWorkflowsPage from './pages/admin/Workflow/AdminWorkflowsPage';
import AdminWorkflowDetailPage from './pages/admin/Workflow/AdminWorkflowDetailPage';
import WorkflowTemplatesPage from './pages/admin/Workflow/WorkflowTemplatesPage';
import CreateWorkflowTemplatePage from './pages/admin/Workflow/CreateWorkflowTemplatePage';
import EditWorkflowTemplatePage from './pages/admin/Workflow/EditWorkflowTemplatePage';

// Staff Pages
import StaffDashboardPage from './pages/staff/DashboardPage';
import StaffMemberListPage from './pages/staff/MemberListPage';
import StaffWorkflowTemplatesPage from './pages/staff/workflow/StaffWorkflowTemplatesPage';

// Error Pages
import NotFoundPage from './pages/errors/NotFoundPage';
import AccessDeniedPage from './pages/errors/AccessDeniedPage';
import TenantError from './pages/TenantError';
import PendingApprovalPage from './pages/PendingApprovalPage';

// Subscription Pages
import SubscriptionPage from './pages/subscription/SubscriptionPage';
import SubscriptionSuccess from './pages/subscription/SubscriptionSuccess';
import SubscriptionCancel from './pages/subscription/SubscriptionCancel';

import RequiredPasswordChange from './pages/auth/RequiredPasswordChange';
import authService from './services/authService';
import AuthRoute from './components/AuthRoute';

// Import RoleBasedRedirect component
import RoleBasedRedirect from './components/RoleBasedRedirect';

function App() {
  const { user, loading: authLoading } = useAuth();
  const { tenant, loading: tenantLoading } = useTenant();

  const [passwordResetRequired, setPasswordResetRequired] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkPasswordReset = async () => {
      try {
        if (authService.getToken()) {
          const user = await authService.getCurrentUser();
          if (user && user.passwordResetRequired) {
            setPasswordResetRequired(true);
          }
        }
      } catch (err) {
        console.error("Error checking password reset status:", err);
      } finally {
        setLoading(false);
      }
    };

    checkPasswordReset();
  }, []);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (passwordResetRequired) {
    return <RequiredPasswordChange onComplete={() => setPasswordResetRequired(false)} />;
  }

  if (authLoading || tenantLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <ThemeProvider>
      <NotificationProvider>
        <MessageProvider>
          <Routes>
            {/* Public Routes */}
            <Route element={<Layout />}>
              <Route path="/" element={user ? <RoleBasedRedirect /> : <LandingPage />} />
              <Route path="/login" element={user ? <Navigate to="/" /> : <LoginPage />} />
              <Route path="/register" element={user ? <Navigate to="/" /> : <RegisterPage />} />
              <Route path="/register-tenant" element={user ? <Navigate to="/" /> : <RegisterTenantPage />} />
              <Route path="/reset-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
              <Route path="/payment-success" element={<Navigate to="/dashboard" />} />
              <Route path="/payment/cancel" element={
                <div style={{padding: '2rem', textAlign: 'center'}}>
                  <h2>Payment Cancelled</h2>
                  <p>Your payment process was cancelled.</p>
                  <button onClick={() => navigate('/dashboard')}>Return to Dashboard</button>
                </div>
              } />
              
              <Route path="/pending-approval" element={<PendingApprovalPage />} />
              <Route path="/marketing-materials" element={<MarketingMaterialsPage />} />
              
            </Route>

            {/* Subscription Routes */}
            <Route path="/subscription" element={<SubscriptionPage />} />
            <Route path="/subscription/success" element={<SubscriptionSuccess />} />
            <Route path="/subscription/cancel" element={<SubscriptionCancel />} />

            {/* Member Routes - Using MemberSidebar instead of TenantLayout */}
            <Route element={<MemberSidebar><Outlet /></MemberSidebar>}>
              <Route path="/member/dashboard" element={<MemberDashboardPage />} />
              <Route path="/member/profile" element={<MemberProfilePage />} />
              <Route path="/member/help" element={<MemberHelpPage />} />
              <Route path="/member/directory" element={<MemberDirectoryPage />} />
              
              <Route path="/member/subscription-plans" element={<MemberPlans />} />
              <Route path="/member/tenant-plans" element={<TenantSubscriptionPlans />} />
              <Route path="/member/my-subscription" element={<MySubscription />} />
              <Route path="/member/subscribe/:planId" element={<MemberSubscriptionCheckout />} />
              <Route path="/member/tenant-subscribe/:planId" element={<SubscribeToTenantPlan />} />
              <Route path="/member/payment-history" element={<PaymentHistory />} />
              <Route path="/member/financial-requests" element={<MemberFinancialRequestsPage />} />
              <Route path="/member/financial-requests/create" element={<CreateFinancialRequestPage />} />
              <Route path="/member/financial-requests/:requestId" element={<MemberFinancialRequestDetailPage />} />
              <Route path="/member/wallet" element={<MemberWalletDashboardPage />} />
              <Route path="/member/reset" element={<MemberReset />} />
              
              {/* Consolidated SkillCoin routes for members */}
              <Route path="/member/skillcoin" element={<Navigate to="/member/skillcoin/wallet" replace />} />
              <Route path="/member/skillcoin/token-history" element={<TokenHistoryPage />} />
              <Route path="/member/skillcoin/:tab" element={<MemberSkillCoinDashboardPage />} />

              {/* Member Messages routes */}
              <Route path="/member/messages" element={<MemberMessagesPage />} />
              <Route path="/member/messages/compose" element={<MemberMessageComposePage />} />
              <Route path="/member/messages/:messageId" element={<MemberMessageDetailPage />} />

              {/* Member Workflow routes */}
              <Route path="/member/workflows" element={<MemberWorkflowsPage />} />
              <Route path="/member/workflows/create" element={<CreateWorkflowRequestPage />} />
              <Route path="/member/workflows/:requestId" element={<MemberWorkflowDetailPage />} />
            </Route>

            {/* Admin Routes - Now using AdminSidebar instead of AdminLayout */}
            <Route element={<AdminSidebar><Outlet /></AdminSidebar>}>
              <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="/admin/dashboard" element={<DashboardPage />} />
              <Route path="/admin/members" element={<MemberListPage />} />
              <Route path="/admin/members/add" element={<AddMemberPage />} />
              <Route path="/admin/members/bulk-upload" element={<BulkMemberUploadPage />} />
              <Route path="/admin/settings" element={<TenantSettingsPage />} />
              <Route path="/admin/analytics" element={<AnalyticsPage />} />
              <Route path="/admin/member-subscription-plans" element={<MemberSubscriptionPlans />} />
              
              {/* Add the missing financial requests routes */}
              <Route path="/admin/financial-requests" element={<AdminFinancialRequestsPage />} />
              <Route path="/admin/financial-requests/:requestId" element={<AdminFinancialRequestDetailPage />} />
              
              <Route path="/admin/member-subscription-plans/add" element={
                <MemberSubscriptionPlanForm 
                  addMemberPlan={() => {}} 
                  updateMemberPlan={() => {}} 
                  clearCurrentPlan={() => {}}
                />
              } />
              <Route path="/admin/member-subscription-plans/:id" element={
                <MemberSubscriptionPlanItem 
                  plan={{}} 
                  editMemberPlan={() => {}} 
                  deleteMemberPlan={() => {}} 
                />
              } />
              <Route path="/admin/plans" element={<AdminPlans />} />
              <Route path="/admin/report-scheduler" element={<FinancialReportScheduler />} />
              <Route path="/admin/subscription/checkout" element={<SubscriptionCheckout />} />
              <Route path="/admin/subscription/success" element={<SubscriptionSuccess />} />
              <Route path="/admin/subscription/cancel" element={<SubscriptionCancel />} />
            
              <Route path="/admin/subscription" element={<SubscriptionManagement />} />
              <Route path="/admin/debt-management" element={<AdminDebtManagementPage />} />
              
              {/* SkillCoin routes for admins - Main route and tab-based navigation */}
              <Route path="/admin/skillcoin" element={<AdminSkillCoinDashboardPage />} />
              <Route path="/admin/skillcoin/:tab" element={<AdminSkillCoinDashboardPage />} />
              
              {/* Add the MemberReset route for admin users */}
              <Route path="/admin/reset" element={<MemberReset />} />

              {/* Admin Messages routes */}
              <Route path="/admin/messages" element={<AdminMessagesPage />} />
              <Route path="/admin/messages/compose" element={<AdminComposePage />} />

              {/* Admin Workflow routes */}
              <Route path="/admin/workflows" element={<AdminWorkflowsPage />} />
              <Route path="/admin/workflows/templates" element={<WorkflowTemplatesPage />} />
              <Route path="/admin/workflows/templates/create" element={<CreateWorkflowTemplatePage />} />
              <Route path="/admin/workflows/templates/edit/:templateId" element={<EditWorkflowTemplatePage />} />
              <Route path="/admin/workflow-dashboard" element={<WorkflowDashboard />} />
            </Route>

            {/* Staff Routes - New section for staff role */}
            <Route element={<StaffSidebar><Outlet /></StaffSidebar>}>
              <Route path="/staff" element={<Navigate to="/staff/dashboard" replace />} />
              <Route path="/staff/dashboard" element={<StaffDashboardPage />} />
              <Route path="/staff/members" element={<StaffMemberListPage />} />
              <Route path="/staff/membership-analytics" element={<MembershipAnalytics />} />
              <Route path="/staff/workflows" element={<AdminWorkflowsPage />} />
              <Route path="/staff/messages" element={<AdminMessagesPage />} />
              <Route path="/staff/messages/compose" element={<AdminComposePage />} />
              <Route path="/staff/reset" element={<MemberReset />} />
              
              {/* Add financial requests routes for staff */}
              <Route path="/staff/financial-requests" element={<AdminFinancialRequestsPage />} />
              <Route path="/staff/financial-requests/:requestId" element={<AdminFinancialRequestDetailPage />} />
              
              {/* Add the staff workflow templates routes */}
              <Route path="/staff/workflows/templates" element={<StaffWorkflowTemplatesPage />} />
              <Route path="/staff/workflows/templates/create" element={<CreateWorkflowTemplatePage isStaffPortal={true} />} />
              <Route path="/staff/workflows/templates/edit/:templateId" element={<EditWorkflowTemplatePage isStaffPortal={true} />} />
              
              {/* Add the missing staff workflow dashboard route */}
              <Route path="/staff/workflow-dashboard" element={<WorkflowDashboard />} />
              
              {/* Add the staff profile page route */}
              <Route path="/staff/profile" element={<MemberProfilePage />} />
            </Route>

            {/* Shared routes that should respect user role - use RoleBasedSidebar */}
            <Route element={<RoleBasedSidebar><Outlet /></RoleBasedSidebar>}>
              {/* Member detail page - should respect the user's role for proper sidebar */}
              <Route path="/admin/members/:id" element={<MemberDetailPage />} />
              <Route path="/staff/members/:id" element={<MemberDetailPage />} />
              
              {/* Other shared pages that should maintain role-based navigation */}
              <Route path="/admin/membership-analytics" element={<MembershipAnalytics />} />
              
              <Route path="/admin/expenses" element={<ExpensesPage />} />
              <Route path="/staff/expenses" element={<ExpensesPage />} />
              
              <Route path="/admin/workflows/:requestId" element={<AdminWorkflowDetailPage />} />
              <Route path="/staff/workflows/:requestId" element={<AdminWorkflowDetailPage />} />
              
              {/* These routes are already defined but were missing from RoleBasedSidebar section */}
              <Route path="/admin/financial-requests/:requestId" element={<AdminFinancialRequestDetailPage />} />
              <Route path="/staff/financial-requests/:requestId" element={<AdminFinancialRequestDetailPage />} />
              
              <Route path="/admin/messages/:messageId" element={<AdminMessageDetailPage />} />
              <Route path="/staff/messages/:messageId" element={<AdminMessageDetailPage />} />
            </Route>

            {/* SuperAdmin Routes - Now using SuperAdminSidebar instead of SuperAdminLayout */}
            <Route element={<SuperAdminSidebar><Outlet /></SuperAdminSidebar>}>
              <Route path="/superadmin/dashboard" element={
                user?.role === 'superadmin' ? <AdminDashboard /> : <Navigate to="/access-denied" />
              } />
              <Route path="/superadmin/tenants" element={
                user?.role === 'superadmin' ? <SuperAdminTenantsPage /> : <Navigate to="/access-denied" />
              } />
              <Route path="/superadmin/tenants/:id" element={
                user?.role === 'superadmin' ? <SuperAdminTenantDetailPage /> : <Navigate to="/access-denied" />
              } />
              <Route path="/superadmin/tenants/add" element={
                user?.role === 'superadmin' ? <SuperAdminAddTenantPage /> : <Navigate to="/access-denied" />
              } />
              <Route path="/superadmin/tenants/:id/edit" element={
                user?.role === 'superadmin' ? <SuperAdminTenantDetailPage edit={true} /> : <Navigate to="/access-denied" />
              } />
              <Route path="/superadmin/users" element={
                user?.role === 'superadmin' ? <AllUsers /> : <Navigate to="/access-denied" />
              } />
              <Route path="/superadmin/analytics" element={
                user?.role === 'superadmin' ? <AnalyticsPage /> : <Navigate to="/access-denied" />
              } />
              <Route path="/superadmin/settings" element={
                user?.role === 'superadmin' ? <TenantSettingsPage /> : <Navigate to="/access-denied" />
              } />
              <Route path="/superadmin/payments/settings" element={
                user?.role === 'superadmin' ? <SuperAdminPaymentSettings /> : <Navigate to="/access-denied" />
              } />
              <Route path="/superadmin/subscription" element={
                user?.role === 'superadmin' ? <SuperAdminSubscriptions /> : <Navigate to="/access-denied" />
              } />
              <Route path="/superadmin/payments/plans" element={
                user?.role === 'superadmin' ? <SuperAdminPlanManagement /> : <Navigate to="/access-denied" />
              } />
              <Route path="/superadmin/tenants/:tenantId/add-admin" element={
                user?.role === 'superadmin' ? <SuperAdminAddTenantAdminPage /> : <Navigate to="/access-denied" />
              } />
              
              {/* Add the MemberReset route for superadmin users */}
              <Route path="/superadmin/reset" element={<MemberReset />} />
            </Route>

            {/* Error Pages */}
            <Route path="/not-found" element={<NotFoundPage />} />
            <Route path="/access-denied" element={<AccessDeniedPage />} />
            <Route path="/tenant-error" element={<TenantError />} />
            <Route path="*" element={<Navigate to="/not-found" replace />} />
            <Route path="/logout" element={<Navigate to="/login" replace />} />
            <Route path="/change-password" element={<RequiredPasswordChange />} />
          </Routes>
        </MessageProvider>
      </NotificationProvider>
    </ThemeProvider>
  );
}

export default App;